/**
 * One month with attended offline sessions not paid yet (monthly groups).
 *
 * @example
 * const due: OfflineDue = { month: "2026-10", sessions: 4, amount: 300, invoiceId: null };
 */
export type OfflineDue = {
  /** "YYYY-MM" */
  month: string;
  sessions: number;
  amount: number | null;
  invoiceId: number | null;
};

/** Keeps only well-formed months from the API. @example readOfflineDues({ months: [] }); // [] */
export function readOfflineDues(data: unknown): OfflineDue[] {
  const months = (data as { months?: unknown })?.months;

  if (!Array.isArray(months)) return [];

  return months
    .filter(
      (item): item is OfflineDue =>
        Boolean(item) &&
        /^\d{4}-\d{2}$/.test(String(item.month)) &&
        Number(item.sessions) > 0,
    )
    .map(item => ({
      month: item.month,
      sessions: Number(item.sessions),
      amount: Number.isFinite(Number(item.amount)) && item.amount !== null
        ? Number(item.amount)
        : null,
      invoiceId: Number(item.invoiceId) || null,
    }));
}

/**
 * Sum of the months' amounts, or `null` when any month has no price.
 *
 * @example
 * totalOfflineDue([{ amount: 300 }, { amount: 250 }] as OfflineDue[]); // 550
 */
export function totalOfflineDue(months: OfflineDue[]): number | null {
  let total = 0;

  for (const month of months) {
    if (month.amount === null) return null;
    total += month.amount;
  }

  return total;
}

/**
 * Identity of the current dues: a dismissed strip comes back once it changes
 * (a new attended session or a new month).
 *
 * @example
 * offlineDuesSignature([{ month: "2026-10", sessions: 4 }] as OfflineDue[]); // "2026-10:4"
 */
export function offlineDuesSignature(months: OfflineDue[]): string {
  return months.map(month => `${month.month}:${month.sessions}`).join("|");
}

/** Mid-month date of "YYYY-MM", for month-name formatting. @example monthDate("2026-10"); */
export function monthDate(month: string): Date {
  const [year, monthNumber] = month.split("-").map(Number);

  return new Date(Date.UTC(year, monthNumber - 1, 15, 12));
}

/**
 * Translation key for "N sessions" in the given language (Arabic has
 * one / two / few / many forms).
 *
 * @example
 * sessionsCountKey(4, "ar"); // "sessionHistory.duesSessionsFew"
 * sessionsCountKey(11, "ar"); // "sessionHistory.duesSessionsMany"
 */
export function sessionsCountKey(count: number, localeCode: string): string {
  const form = new Intl.PluralRules(localeCode).select(count);

  if (form === "one") return "sessionHistory.duesSessionsOne";
  if (form === "two") return "sessionHistory.duesSessionsTwo";
  if (form === "few") return "sessionHistory.duesSessionsFew";

  return localeCode.toLowerCase().startsWith("ar")
    ? "sessionHistory.duesSessionsMany"
    : "sessionHistory.duesSessionsFew";
}
